import { z } from 'zod';
/**
 * Contrats CRUD des FILTRES ENREGISTRÉS (« filtres enregistrés + expression booléenne »).
 * Source UNIQUE front + back.
 *
 * Un filtre enregistré = un nom, une icône optionnelle (Heroicons outline) et une
 * EXPRESSION de filtre en TEXTE (`expr`, mini-langage de `filter-expr.ts`). L'expression
 * est stockée telle quelle (texte) ; sa SYNTAXE n'est PAS validée par ces schémas Zod —
 * seule la LONGUEUR l'est. La validation syntaxique se fait via `parseFilterExpr` côté
 * API (avant persistance) et côté UI (retour amical à l'utilisateur). Les filtres sont
 * ORDONNÉS par `position` (réordonnables par glisser-déposer).
 *
 * Les schémas de CORPS/QUERY de route (`savedFilterCreateSchema`, `savedFilterUpdateSchema`,
 * `savedFilterReorderSchema`) NE CONTIENNENT AUCUN `.transform` (piège Zod v4 →
 * `z.toJSONSchema` lève au boot ; cf. `route-json-schema.test.ts`). La normalisation
 * « chaîne vide → null » éventuelle se fait dans le HANDLER de route.
 */
/** Longueur maximale du NOM d'un filtre enregistré. */
export const SAVED_FILTER_NAME_MAX = 80;
/** Longueur maximale de l'EXPRESSION texte (`expr`) d'un filtre enregistré. */
export const SAVED_FILTER_EXPR_MAX = 2000;
/** Plafond du nombre d'ids acceptés par un réordonnancement (garde-fou DB). */
export const SAVED_FILTER_REORDER_MAX = 200;
/**
 * Jeu restreint de noms d'ICÔNES Heroicons (outline 24px) proposés pour un filtre
 * enregistré. Allow-list (jamais d'entrée brute) — l'UI mappe chaque nom vers son SVG
 * inline. Cohérent avec le vocabulaire visuel de l'app (galerie photo/vidéo, tags, notes).
 */
export const savedFilterIconSchema = z.enum([
    'funnel',
    'star',
    'camera',
    'video-camera',
    'sparkles',
    'tag',
    'sun',
    'heart',
    'bookmark',
    'photo',
]);
/**
 * Corps de `POST /api/libraries/:id/saved-filters` (création). `expr` est bornée en
 * longueur seulement ; sa validité syntaxique relève de `parseFilterExpr` (handler/UI).
 * Aucun `.transform` (garde boot `z.toJSONSchema`).
 */
export const savedFilterCreateSchema = z.object({
    name: z.string().trim().min(1).max(SAVED_FILTER_NAME_MAX),
    icon: savedFilterIconSchema.optional(),
    expr: z.string().trim().min(1).max(SAVED_FILTER_EXPR_MAX),
});
/**
 * Corps de `PATCH /api/libraries/:id/saved-filters/:filterId` (mise à jour PARTIELLE) :
 * tous les champs sont optionnels. `icon` est `nullable` pour RETIRER l'icône (poser
 * `null`) — distinct de l'absence de champ (« ne pas toucher »). Aucun `.transform`.
 */
export const savedFilterUpdateSchema = z.object({
    name: z.string().trim().min(1).max(SAVED_FILTER_NAME_MAX).optional(),
    icon: savedFilterIconSchema.nullable().optional(),
    expr: z.string().trim().min(1).max(SAVED_FILTER_EXPR_MAX).optional(),
    /**
     * VERROUILLAGE OPTIMISTE (facultatif) : l'`updatedAt` du filtre TEL QUE LU par le
     * client. S'il est fourni et ne correspond plus à l'`updated_at` en base (un autre
     * onglet/éditeur a modifié l'expression entre-temps), le PATCH est refusé en 409
     * `STALE_UPDATE` sans rien écrire — plutôt que d'écraser silencieusement. Absent ⇒
     * ancien comportement (dernier arrivé gagne).
     *
     * Borné en LONGUEUR seulement, sans `.transform` (piège boot `z.toJSONSchema`) : la
     * validation « date analysable » vit dans le handler de route.
     */
    expectedUpdatedAt: z.string().min(1).max(64).optional(),
});
/**
 * Corps de `PUT /api/libraries/:id/saved-filters/order` (réordonnancement) : l'ORDRE
 * COMPLET des ids (le rang de chaque filtre = son index dans le tableau). Plafonné à
 * `SAVED_FILTER_REORDER_MAX`. Aucun `.transform`.
 */
export const savedFilterReorderSchema = z.object({
    ids: z.array(z.uuid()).max(SAVED_FILTER_REORDER_MAX),
});
/**
 * DTO d'un filtre enregistré renvoyé au client. `icon` `null` = aucune icône ;
 * `position` = rang 0-based dans la liste ordonnée ; `createdAt`/`updatedAt` en ISO 8601
 * avec offset.
 */
export const savedFilterSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    icon: savedFilterIconSchema.nullable(),
    expr: z.string(),
    position: z.number().int().nonnegative(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
});
/** Résultat de `GET /api/libraries/:id/saved-filters` : la liste ordonnée des filtres. */
export const savedFilterListSchema = z.object({
    filters: z.array(savedFilterSchema),
});
//# sourceMappingURL=saved-filter.js.map